import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, InputField } from "../../components/ui";
import type { GtmSequenceMessageMode } from "../api/GtmApi";
import type { SequenceMessageStep } from "./sequenceToFlow";

const STEP_LABELS: Record<SequenceMessageStep, string> = {
  first: "First message",
  follow_up_2: "Follow-up 2",
  follow_up_3: "Follow-up 3",
};

type Props = {
  step: SequenceMessageStep;
  messageMode: GtmSequenceMessageMode;
  messageBody: string;
  saving?: boolean;
  error?: string | null;
  /** When true, render form fields only (parent supplies chrome). */
  embedded?: boolean;
  onClose: () => void;
  onSave: (next: { messageMode: GtmSequenceMessageMode; messageBody: string }) => void;
};

export default function SequenceMessageStepEditor({
  step,
  messageMode,
  messageBody,
  saving,
  error,
  embedded = false,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<GtmSequenceMessageMode>(messageMode);
  const [body, setBody] = useState(messageBody);

  useEffect(() => {
    setMode(messageMode);
    setBody(messageBody);
  }, [step, messageMode, messageBody]);

  const form = (
    <>
      {!embedded ? (
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t(STEP_LABELS[step])}</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("Choose how this LinkedIn message is written for every lead in the play.")}
            </p>
          </div>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-800"
            onClick={onClose}
            aria-label={t("Close")}
          >
            ×
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          {t("Choose how this LinkedIn message is written for every lead in the play.")}
        </p>
      )}

      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{t("Mode")}</span>
        <div className="flex flex-col gap-2">
          {(
            [
              ["ai", "Generate with AI"],
              ["custom", "Custom message"],
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                mode === value ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name={`message-mode-${step}`}
                className="mt-0.5"
                checked={mode === value}
                onChange={() => setMode(value)}
              />
              <span className="font-medium text-gray-900">{t(label)}</span>
            </label>
          ))}
        </div>
      </div>

      {mode === "custom" ? (
        <div className="space-y-1">
          <InputField
            mode="textarea"
            rows={8}
            label={t("Message")}
            value={body}
            onChange={setBody}
            placeholder={t("Hi {name}, saw your work at {company} as {title}…")}
          />
          <p className="text-xs text-gray-500">{t("Placeholders: {name}, {title}, {company}")}</p>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          {t("AI drafts each message from the play angle and Brain context when the sequence runs.")}
        </p>
      )}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className={`flex justify-end gap-2 ${embedded ? "" : "mt-auto border-t border-gray-100 pt-3"}`}>
        {!embedded ? (
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            {t("Cancel")}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          isLoading={saving}
          onClick={() => onSave({ messageMode: mode, messageBody: body })}
        >
          {t("Save")}
        </Button>
      </div>
    </>
  );

  if (embedded) {
    return <div className="flex flex-col gap-3 border-t border-gray-100 pt-3">{form}</div>;
  }

  return (
    <aside className="flex w-full max-w-sm flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {form}
    </aside>
  );
}
